// import * as Sentry from '@sentry/react';
import * as fetchIntercept from 'fetch-intercept';
import * as Storage from '../services/storage';
import { cleanUserSession } from './auth';
import { dangerNotification } from './notification';

fetchIntercept.register({
  request: function (url, config) {
    if (config && config.headers) {
      config.headers.Authorization = Storage.get('id_token');
      config.headers['X-External-Login'] = Storage.get('external_login');
      return [url, config];
    }
    return [url, config];
  }
});

function maintenanceUrl(base: string): string {
  return `${process.env.REACT_APP_RV_URL}/manutencao`;
}

function homeUrl(base: string): string {
  return `${process.env.REACT_APP_RV_URL}/`;
}

export const request = async (
  endpoint: string,
  method: string,
  payload: any = {},
  headers: any = {},
  override?: boolean | false
): Promise<any> => {
  const config: any = {
    method,
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      ...headers
    }
  };

  if (method !== 'GET' && method !== 'DELETE') {
    config.body = JSON.stringify(payload);
  }

  let data: any;
  let attempt = 0;
  const maxAttempts = 2;

  while (attempt < maxAttempts) {
    try {
      const response = await fetch(
        override ? endpoint : `${process.env.REACT_APP_API_URL}${endpoint}`,
        config
      );

      const { status, url } = response;
      const currentUrl = window.location.href;

      if (status === 503 && currentUrl !== maintenanceUrl(url)) {
        window.location.assign(maintenanceUrl(url));
        return;
      }

      if (currentUrl === maintenanceUrl(url)) {
        window.location.assign(homeUrl(url));
        return;
      }

      if (status === 401 && endpoint !== '/auth/login') {
        cleanUserSession();
        dangerNotification(
          'Erro',
          'Sua sessão expirou, por favor, faça o login novamente para acessar.'
        );
        return;
      }

      data = await response.json();
      return { status: response.status, data };
    } catch (error) {
      console.error('Erro ao fazer a requisição', {
        endpoint,
        method,
        error,
        payload: method !== 'GET' && method !== 'DELETE' ? payload : undefined
      });

      if (typeof error === 'object' && error !== null && 'message' in error) {
        if (attempt < maxAttempts - 1) {
          attempt++;
          console.warn(`Tentativa ${attempt} de ${maxAttempts}`);
          continue; // Tentar novamente se o erro contém a mensagem
        } else {
          console.log('Erro na requisição', error.message);
          dangerNotification('Erro', error.message as string);
          throw error;
        }
      } else {
        const customError = new Error('Um erro inesperado ocorreu durante a requisição');
        throw customError;
      }
    }
  }
};

export const requestFile = async (
  enpoint: string,
  formData: FormData,
  headers: any = {}
) => {
  const config = {
    method: 'POST',
    headers: {
      ...headers
    },
    body: formData
  };

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${enpoint}`, config);
    const { status } = response;
    const data = await response.json();

    return { status, data };
  } catch (error) {
    console.error('Erro ao fazer a requisição', {
      enpoint,
      error
    });

    if (typeof error === 'object' && error !== null && 'message' in error) {
      dangerNotification(
        'Erro',
        'Ocorreu um problema na sua requisição. Tente novamente mais tarde.'
      );
      throw error;
    } else {
      const customError = new Error('Um erro inesperado ocorreu durante a requisição');
      throw customError;
    }
  }
};

export const get = async (
  endpoint: string,
  headers: any = {},
  override?: boolean | false
): Promise<any> => {
  try {
    const response = await request(endpoint, 'GET', {}, headers, override);

    if (response?.statusCode >= 400 || response?.status >= 400) {
      throw new Error(
        response.data && response.data.message
          ? response.data.message
          : `API ERROR: ${response}`
      );
    }

    return response?.data;
  } catch (e) {
    throw e;
  }
};

export const post = async (
  endpoint: string,
  payload: any,
  headers: any = {}
): Promise<any> => {
  try {
    const response = await request(endpoint, 'POST', payload, headers, false);
    if (response?.statusCode >= 400 || response?.status >= 400) {
      throw response.data;
    }

    return response.data;
  } catch (e) {
    throw e;
  }
};

export const put = async (
  endpoint: string,
  payload: any,
  headers: any = {}
): Promise<any> => {
  try {
    const response = await request(endpoint, 'PUT', payload, headers);

    if (response?.statusCode >= 400 || response?.status >= 400) {
      throw new Error(
        response.data?.message ? response.data?.message : `API ERROR: ${response}`
      );
    }

    return response?.data;
  } catch (e) {
    throw e;
  }
};

export const patch = async (
  endpoint: string,
  payload: any,
  headers: any = {}
): Promise<any> => {
  try {
    const response = await request(endpoint, 'PATCH', payload, headers, false);

    if (response?.statusCode >= 400 || response?.status >= 400) {
      throw new Error(
        response?.data?.message ? response?.data?.message : `API ERROR: ${response}`
      );
    }

    return response?.data;
  } catch (e) {
    throw e;
  }
};

export const remove = async (endpoint: string, headers: any = {}): Promise<any> => {
  try {
    const response = await request(endpoint, 'DELETE', headers);

    if (response?.statusCode >= 400 || response?.status >= 400) {
      throw new Error(
        response?.data?.message ? response?.data?.message : `API ERROR: ${response}`
      );
    }

    return response.data;
  } catch (e) {
    throw e;
  }
};
